import React, { Fragment, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { Link } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import CopyrightIcon from '@mui/icons-material/Copyright';
import generateInterCityPDF from './Bill';
import '../shared.css';

const InterCityStockTransferDetails = () => {
    const { id } = useParams();
    const { isAuthenticated, companyId, branchId, userId, permissions, token, logout } = useAuth();

    const [formData, setFormData]             = useState(null);
    const [invoiceDetails, setInvoiceDetails] = useState([]);
    const [dropdownBranchOptions, setDropdownBranchOptions]   = useState([]);
    const [dropdownSalesManOptions, setDropdownSalesManOptions] = useState([]);
    const [selectedToBranch, setSelectedToBranch] = useState('');
    const [selectedSalesMan, setSelectedSalesMan] = useState('');
    const [loadError, setLoadError]           = useState('');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        fetchData('InterCityStockTransferApi', `/SalesMan/${companyId}/${branchId}`, token)
            .then(r => setDropdownSalesManOptions(r.response_data || []))
            .catch(console.error);

        fetchData('InterCityStockTransferApi', `/Branch/${companyId}/${branchId}`, token)
            .then(r => setDropdownBranchOptions(r.response_data || []))
            .catch(console.error);
    }, []);

    useEffect(() => {
        if (!id) return;
        fetchData('InterCityStockTransferApi', `/${id}`, token)
            .then(r => {
                console.log('InterCityStockTransfer API response:', JSON.stringify(r));
                const rd = r?.response_data;
                if (!rd) { setLoadError('No data returned from server.'); return; }

                // Shape 1: { InterCityStockTransfer, InterCityStockTransferDetails }
                if (rd.interCityStockTransfer) {
                    setFormData(rd.interCityStockTransfer);
                    setInvoiceDetails(rd.InterCityStockTransferDetails || []);
                    setSelectedToBranch(rd.interCityStockTransfer.ToBranchId || '');
                    setSelectedSalesMan(rd.interCityStockTransfer.SalesManId || '');
                    return;
                }
                
                // Shape 5: any object — use it directly and log for debugging
                console.warn('Unknown response shape, using rd directly:', rd);
                setFormData(rd);
                setInvoiceDetails([]);
                setSelectedToBranch(rd.ToBranchId || '');
                setSelectedSalesMan(rd.SalesManId || '');
            })
            .catch(err => {
                console.error('Error fetching InterCityStockTransfer details:', err);
                setLoadError('Failed to load data. Please try again.');
            });
    }, [id]);

    const fmt = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        return isNaN(d) ? '' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const sf = (val) => {
        const n = parseFloat(val);
        return isNaN(n) ? '0.00' : n.toFixed(2);
    };

    const handlePrint = async () => {
        try {
            const response = await fetchData(
                'InterCityStockTransferApi',
                `/SalePrint/${id}/${branchId}`,
                token
            );
            generateInterCityPDF(response.response_data);
        } catch (error) {
            console.error('Error printing Inter City Stock Transfer:', error);
        }
    };

    // ── Shared header (shown in all states) ───────────────────────────────
    const PageHeader = () => (
        <div className="pg-header">
            <h5 className="pg-title">Inter City Stock Transfer – Details</h5>
            <div className="pg-header-actions">
                {formData && (
                    <button className="pg-btn pg-btn-outline" onClick={handlePrint}>
                        <PrintIcon style={{ fontSize: 15 }} /> Print
                    </button>
                )}
                <Link to="/InterCityStockTransferIndex" className="pg-btn pg-btn-danger">
                    <CloseIcon /> Back to List
                </Link>
            </div>
        </div>
    );

    // ── Loading state ──────────────────────────────────────────────────────
    if (!formData && !loadError) {
        return (
            <div className="pg-container">
                <PageHeader />
                <div style={{ padding: 24, color: '#94a3b8', fontSize: 13 }}>Loading…</div>
            </div>
        );
    }

    // ── Error state ────────────────────────────────────────────────────────
    if (loadError) {
        return (
            <div className="pg-container">
                <PageHeader />
                <div className="pg-alert pg-alert-error" style={{ margin: 16 }}>{loadError}</div>
            </div>
        );
    }

    // ── Main content ───────────────────────────────────────────────────────
    return (
        <Fragment>
            <div className="pg-container">
                <PageHeader />

                <div className="pg-card">
                    {/* ── Row 1: Bill / Branch info ── */}
                    <div className="pg-hpanel">
                        <div className="pg-hcell" style={{ flex: '1 1 0' }}>
                            <span className="pg-hcell-label">Bill Date</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="date"
                                value={fmt(formData.BillDate)} disabled />
                        </div>
                        <div className="pg-hcell" style={{ flex: '1 1 0' }}>
                            <span className="pg-hcell-label">Bill Number</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="text"
                                value={`${formData.BillPrefix || ''}/${formData.BillNumber || ''}/${formData.FinYear || ''}`}
                                readOnly />
                        </div>
                        <div className="pg-hcell" style={{ flex: '2 1 0' }}>
                            <span className="pg-hcell-label">To Branch</span>
                            <select className="pg-hcell-select" value={selectedToBranch} disabled>
                                <option value="">-</option>
                                {dropdownBranchOptions.map(b => (
                                    <option key={b.Id} value={b.Id}>{b.Name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="pg-hcell" style={{ flex: '2 1 0' }}>
                            <span className="pg-hcell-label">Sales Man</span>
                            <select className="pg-hcell-select" value={selectedSalesMan} disabled>
                                <option value="">-</option>
                                {dropdownSalesManOptions.map(s => (
                                    <option key={s.Id} value={s.Id}>{s.Name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="pg-hcell" style={{ flex: '0 0 80px' }}>
                            <span className="pg-hcell-label">Total Qty</span>
                            <input className="pg-hcell-input pg-hcell-readonly text-right" type="text"
                                value={formData.TotalQuantity || 0} readOnly />
                        </div>
                    </div>

                    {/* ── Row 2: Customer info ── */}
                    <div className="pg-hpanel" style={{ marginTop: 8 }}>
                        <div className="pg-hcell" style={{ flex: '3 1 0' }}>
                            <span className="pg-hcell-label">Customer Name</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="text"
                                value={formData.CustomerName || ''} readOnly />
                        </div>
                        <div className="pg-hcell" style={{ flex: '3 1 0' }}>
                            <span className="pg-hcell-label">Customer Address</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="text"
                                value={formData.CustomerAddress || ''} readOnly />
                        </div>
                        <div className="pg-hcell" style={{ flex: '2 1 0' }}>
                            <span className="pg-hcell-label">GST Number</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="text"
                                value={formData.GSTNumber || ''} readOnly />
                        </div>
                        <div className="pg-hcell" style={{ flex: '2 1 0' }}>
                            <span className="pg-hcell-label">Contact Number</span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="text"
                                value={formData.ContactNumber || ''} readOnly />
                        </div>
                    </div>

                    {/* ── Item table ── */}
                    <div className="pg-invoice-table-wrap">
                        <table className="pg-invoice-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Item</th>
                                    <th className="right">Qty</th>
                                    <th className="right">Rate</th>
                                    <th className="right">Disc %</th>
                                    <th className="right">Disc Amt</th>
                                    <th className="right">Addt'l Disc %</th>
                                    <th className="right">Addt'l Disc Amt</th>
                                    <th className="right">Rate After Disc</th>
                                    <th className="right">GST %</th>
                                    <th className="right">Taxable</th>
                                    <th className="right">CGST</th>
                                    <th className="right">SGST</th>
                                    <th className="right">IGST</th>
                                    <th className="right">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoiceDetails.length === 0
                                    ? <tr><td colSpan={15} style={{ textAlign: 'center', color: '#94a3b8', padding: 14 }}>No items found.</td></tr>
                                    : invoiceDetails.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ textAlign: 'center', width: 36 }}>{index + 1}</td>
                                            <td>{item.ItemName}</td>
                                            <td className="right">{item.Quantity}</td>
                                            <td className="right">{item.Rate}</td>
                                            <td className="right">{item.DiscountPercentage}</td>
                                            <td className="right">{item.DiscountAmount}</td>
                                            <td className="right" style={{ color: '#15803d', fontWeight: 600 }}>{item.AdditionalDiscountPercentage}</td>
                                            <td className="right" style={{ color: '#15803d' }}>{item.AdditionalDiscountAmount}</td>
                                            <td className="right">{item.RateAfterDiscount}</td>
                                            <td className="right">{item.GSTRate}</td>
                                            <td className="right">{item.TaxableAmount}</td>
                                            <td className="right">{item.CGSTAmount}</td>
                                            <td className="right">{item.SGSTAmount}</td>
                                            <td className="right">{item.IGSTAmount}</td>
                                            <td className="right" style={{ fontWeight: 700 }}>{item.TotalAmount}</td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </div>

                    {/* ── Totals Panel ── */}
                    <div className="pg-totals-panel">
                        <div className="pg-totals-left">
                            <div className="pg-totals-summary">
                                <div className="pg-tsum-row"><span className="pg-tsum-label">Total Amount</span><span className="pg-tsum-value">{sf(formData.TotalAmount)}</span></div>
                                <div className="pg-tsum-row"><span className="pg-tsum-label">Discount</span><span className="pg-tsum-value">{sf(formData.DiscountAmount)}</span></div>
                                <div className="pg-tsum-row"><span className="pg-tsum-label">Net Amount</span><span className="pg-tsum-value">{sf(formData.TaxableAmount)}</span></div>
                                <div className="pg-tsum-row"><span className="pg-tsum-label">Total Qty</span><span className="pg-tsum-value">{formData.TotalQuantity || 0}</span></div>
                            </div>
                        </div>
                        <div className="pg-totals-divider" />
                        <div className="pg-totals-right">
                            <div className="pg-totals-remarks">
                                <label className="pg-tpay-label">Remarks</label>
                                <textarea className="pg-remarks-textarea" value={formData.Remarks || ''} readOnly />
                            </div>
                            <div className="pg-totals-gst-row">
                                <div className="pg-tgst-cell"><span className="pg-tgst-label">CGST</span><span className="pg-tgst-value">{sf(formData.CGSTAmount)}</span></div>
                                <div className="pg-tgst-cell"><span className="pg-tgst-label">SGST</span><span className="pg-tgst-value">{sf(formData.SGSTAmount)}</span></div>
                                <div className="pg-tgst-cell"><span className="pg-tgst-label">IGST</span><span className="pg-tgst-value">{sf(formData.IGSTAmount)}</span></div>
                                <div className="pg-tgst-cell"><span className="pg-tgst-label">Round Off</span><span className="pg-tgst-value">{sf(formData.RoundOff)}</span></div>
                            </div>
                            <div className="pg-grand-total-bar">
                                <span className="pg-gt-label">Grand Total</span>
                                <span className="pg-gt-value">{sf(formData.GrandTotal)}</span>
                            </div>
                            <div className="pg-totals-pay-row">
                                <div className="pg-tpay-cell"><label className="pg-tpay-label">Cash</label><input className="pg-tpay-input" type="text" value={sf(formData.CashAmount)} readOnly /></div>
                                <div className="pg-tpay-cell"><label className="pg-tpay-label">Card</label><input className="pg-tpay-input" type="text" value={sf(formData.CardAmount)} readOnly /></div>
                                <div className="pg-tpay-cell"><label className="pg-tpay-label">UPI / RTGS / Other</label><input className="pg-tpay-input" type="text" value={sf(formData.UPIAmount)} readOnly /></div>
                                <div className="pg-tpay-cell"><label className="pg-tpay-label">Balance</label><input className="pg-tpay-input" type="text" value={sf(formData.BalanceAmount)} readOnly /></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
            </div>
        </Fragment>
    );
};

export default InterCityStockTransferDetails;
